import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CheckIcon from '@mui/icons-material/Check';

/**
 * Parsea el contenido del portapapeles (HTML de Word o texto plano TSV/CSV)
 * y extrae una estructura tabular { title, headers, rows }.
 */
export const parseTableFromClipboardData = (clipboardData) => {
  if (!clipboardData) return null;

  // 1. Intentar con HTML (Word y Excel incluyen HTML con <table class="MsoNormalTable">)
  const html = clipboardData.getData ? clipboardData.getData('text/html') : '';
  if (html && (html.includes('<table') || html.includes('<TABLE'))) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const table = doc.querySelector('table');
      if (table) {
        const trs = Array.from(table.querySelectorAll('tr'));
        if (trs.length > 0) {
          const rawRows = [];
          trs.forEach((tr) => {
            const cells = Array.from(tr.querySelectorAll('th, td'));
            const rowValues = cells.map((cell) => {
              // Limpiar espacios en blanco, caracteres de salto de línea de Word y &nbsp;
              return (cell.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            });
            if (rowValues.some((v) => v !== '')) {
              rawRows.push(rowValues);
            }
          });

          if (rawRows.length > 0) {
            const maxCols = Math.max(...rawRows.map((r) => r.length));
            if (maxCols > 0) {
              const headers = rawRows[0].map((h, i) => h || `Columna ${i + 1}`);
              while (headers.length < maxCols) {
                headers.push(`Columna ${headers.length + 1}`);
              }

              const dataRows = rawRows.slice(1).map((r) => {
                const row = [...r];
                while (row.length < maxCols) row.push('');
                return row;
              });

              if (dataRows.length === 0) {
                dataRows.push(new Array(maxCols).fill(''));
              }

              return {
                title: '',
                headers,
                rows: dataRows,
              };
            }
          }
        }
      }
    } catch (e) {
      console.warn('Error al parsear HTML de Word:', e);
    }
  }

  // 2. Intentar con texto plano delimitado por tabulaciones (TSV) - estándar de Word y Excel al copiar celdas
  const text = clipboardData.getData
    ? clipboardData.getData('text/plain')
    : typeof clipboardData === 'string'
    ? clipboardData
    : '';

  if (text) {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length > 0) {
      const hasTabs = lines.some((l) => l.includes('\t'));
      const delimiter = hasTabs
        ? '\t'
        : lines[0].includes(';')
        ? ';'
        : lines[0].includes(',')
        ? ','
        : null;

      if (delimiter) {
        const rawRows = lines.map((l) => l.split(delimiter).map((c) => c.trim()));
        const maxCols = Math.max(...rawRows.map((r) => r.length));

        // Considerar tabla si hay múltiples columnas o múltiples filas tabuladas
        if (maxCols >= 2 || (maxCols === 1 && rawRows.length > 1 && hasTabs)) {
          const headers = rawRows[0].map((h, i) => h || `Columna ${i + 1}`);
          while (headers.length < maxCols) {
            headers.push(`Columna ${headers.length + 1}`);
          }

          const dataRows = rawRows.slice(1).map((r) => {
            const row = [...r];
            while (row.length < maxCols) row.push('');
            return row;
          });

          if (dataRows.length === 0) {
            dataRows.push(new Array(maxCols).fill(''));
          }

          return {
            title: '',
            headers,
            rows: dataRows,
          };
        }
      }
    }
  }

  return null;
};

/**
 * Normaliza cualquier formato previo de objetivo (string plano o JSON enriquecido)
 * en una estructura estandarizada de bloques.
 */
export const normalizeObjetivo = (val) => {
  if (!val) {
    return {
      isRich: true,
      textFallback: '',
      blocks: [
        {
          id: 'b-init-1',
          type: 'text',
          content: '',
        },
      ],
    };
  }

  // Si ya es un objeto estructurado
  if (typeof val === 'object' && val !== null) {
    if (Array.isArray(val.blocks)) {
      return {
        isRich: true,
        textFallback: val.textFallback || extractPlainTextFromBlocks(val.blocks),
        blocks: val.blocks.length > 0 ? val.blocks : [{ id: 'b-1', type: 'text', content: '' }],
      };
    }
  }

  // Si es un string que podría contener JSON
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && Array.isArray(parsed.blocks)) {
          return {
            isRich: true,
            textFallback: parsed.textFallback || extractPlainTextFromBlocks(parsed.blocks),
            blocks: parsed.blocks,
          };
        }
      } catch (e) {
        // No es JSON válido, tratar como texto plano
      }
    }

    // Texto plano ordinario (ej. desde actas antiguas o IA)
    return {
      isRich: true,
      textFallback: val,
      blocks: [
        {
          id: 'b-init-' + Date.now(),
          type: 'text',
          content: val,
        },
      ],
    };
  }

  return {
    isRich: true,
    textFallback: String(val),
    blocks: [{ id: 'b-default', type: 'text', content: String(val) }],
  };
};

/**
 * Extrae texto plano legible a partir de los bloques estructurados
 */
export const extractPlainTextFromBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return '';
  const lines = [];

  blocks.forEach((b) => {
    if (!b) return;
    if (b.type === 'text' && b.content) {
      lines.push(b.content.trim());
    } else if (b.type === 'table') {
      const hasValidTitle =
        b.title &&
        b.title.trim() !== '' &&
        !b.title.toLowerCase().includes('tabla importada') &&
        b.title.toLowerCase() !== 'tabla de datos';
      if (hasValidTitle) {
        lines.push(`[${b.title}]`);
      }
      if (Array.isArray(b.headers)) {
        lines.push(b.headers.join(' | '));
      }
      if (Array.isArray(b.rows)) {
        b.rows.forEach((r) => {
          if (Array.isArray(r)) lines.push(r.join(' | '));
        });
      }
    } else if (b.type === 'callout') {
      const title = b.title ? `${b.title}: ` : '';
      lines.push(`> ${title}${b.content || ''}`);
    } else if (b.type === 'bullet_list' && Array.isArray(b.items)) {
      b.items.forEach((item) => lines.push(`• ${item}`));
    }
  });

  return lines.filter(Boolean).join('\n\n');
};

/**
 * Genera un ID único para un nuevo bloque
 */
const generateId = () => 'b-' + Math.random().toString(36).substr(2, 9);

const ObjetivoRichEditor = ({ value, onChange, placeholder }) => {
  // Estado normalizado de bloques
  const [data, setData] = useState(() => normalizeObjetivo(value));
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'

  // Estados para diálogo de pegar tabla desde Word / Excel
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pastePreviewTable, setPastePreviewTable] = useState(null);
  const [pasteError, setPasteError] = useState('');

  // Notificación tipo toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Sincronizar si cambia el prop value desde el exterior (ej: IA completando el objetivo)
  useEffect(() => {
    const norm = normalizeObjetivo(value);
    const currentPlain = extractPlainTextFromBlocks(data.blocks);
    const newPlain = norm.textFallback;
    if (currentPlain !== newPlain && (!data.blocks || (data.blocks.length <= 1 && !data.blocks[0]?.content))) {
      setData(norm);
    }
  }, [value]);

  // Propagar cambios hacia el padre
  const notifyChange = (newBlocks) => {
    const plain = extractPlainTextFromBlocks(newBlocks);
    const updated = {
      isRich: true,
      textFallback: plain,
      blocks: newBlocks,
    };
    setData(updated);
    if (onChange) {
      onChange(updated);
    }
  };

  // --- Operaciones de Bloques ---
  const handleAddTextBlock = () => {
    const newBlocks = [
      ...data.blocks,
      {
        id: generateId(),
        type: 'text',
        content: '',
      },
    ];
    notifyChange(newBlocks);
  };

  const handleAddTableBlock = (customTable = null) => {
    const tableData = customTable || {
      title: '',
      headers: ['Ítem / Entregable', 'Descripción', 'Responsable', 'Fecha / Plazo'],
      rows: [
        ['1', 'Definición de requerimientos', 'Líder Técnico', 'Semana 1'],
        ['2', 'Revisión y pruebas', 'Equipo QA', 'Semana 2'],
      ],
    };

    const newBlocks = [
      ...data.blocks,
      {
        id: generateId(),
        type: 'table',
        title: tableData.title || '',
        headers: tableData.headers,
        rows: tableData.rows,
        headerColor: '#f1f5f9',
      },
    ];
    notifyChange(newBlocks);
  };

  const handleAddCalloutBlock = () => {
    const newBlocks = [
      ...data.blocks,
      {
        id: generateId(),
        type: 'callout',
        variant: 'info',
        title: 'Nota o Conclusión Clave',
        content: 'Especifique aquí cualquier aclaración relevante, hito crítico o condición de éxito de la reunión.',
      },
    ];
    notifyChange(newBlocks);
  };

  const handleAddBulletListBlock = () => {
    const newBlocks = [
      ...data.blocks,
      {
        id: generateId(),
        type: 'bullet_list',
        items: ['Primer aspecto o alcance clave', 'Segundo aspecto o alcance clave'],
      },
    ];
    notifyChange(newBlocks);
  };

  const handleRemoveBlock = (blockId) => {
    const newBlocks = data.blocks.filter((b) => b.id !== blockId);
    if (newBlocks.length === 0) {
      newBlocks.push({ id: generateId(), type: 'text', content: '' });
    }
    notifyChange(newBlocks);
  };

  const handleMoveBlock = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= data.blocks.length) return;
    const newBlocks = [...data.blocks];
    const [moved] = newBlocks.splice(index, 1);
    newBlocks.splice(targetIndex, 0, moved);
    notifyChange(newBlocks);
  };

  // --- Actualizadores por tipo de bloque ---
  const handleUpdateText = (blockId, newContent) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        return { ...b, content: newContent };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleUpdateCallout = (blockId, field, val) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        return { ...b, [field]: val };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleUpdateBulletItem = (blockId, itemIndex, val) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const items = [...b.items];
        items[itemIndex] = val;
        return { ...b, items };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleAddBulletItem = (blockId) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        return { ...b, items: [...b.items, ''] };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleRemoveBulletItem = (blockId, itemIndex) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const items = b.items.filter((_, idx) => idx !== itemIndex);
        return { ...b, items: items.length > 0 ? items : [''] };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  // --- Operaciones de Tabla ---
  const handleUpdateTableTitle = (blockId, title) => {
    const newBlocks = data.blocks.map((b) => (b.id === blockId ? { ...b, title } : b));
    notifyChange(newBlocks);
  };

  const handleUpdateTableHeader = (blockId, colIndex, val) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const headers = [...b.headers];
        headers[colIndex] = val;
        return { ...b, headers };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleUpdateTableCell = (blockId, rowIndex, colIndex, val) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const rows = b.rows.map((row, rIdx) => {
          if (rIdx === rowIndex) {
            const newRow = [...row];
            newRow[colIndex] = val;
            return newRow;
          }
          return row;
        });
        return { ...b, rows };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleAddTableRow = (blockId) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const emptyRow = new Array(b.headers.length).fill('');
        return { ...b, rows: [...b.rows, emptyRow] };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleRemoveTableRow = (blockId, rowIndex) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const rows = b.rows.filter((_, idx) => idx !== rowIndex);
        return { ...b, rows: rows.length > 0 ? rows : [new Array(b.headers.length).fill('')] };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleAddTableCol = (blockId) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        const newColNum = b.headers.length + 1;
        const headers = [...b.headers, `Columna ${newColNum}`];
        const rows = b.rows.map((row) => [...row, '']);
        return { ...b, headers, rows };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  const handleRemoveTableCol = (blockId, colIndex) => {
    const newBlocks = data.blocks.map((b) => {
      if (b.id === blockId) {
        if (b.headers.length <= 1) return b;
        const headers = b.headers.filter((_, idx) => idx !== colIndex);
        const rows = b.rows.map((row) => row.filter((_, idx) => idx !== colIndex));
        return { ...b, headers, rows };
      }
      return b;
    });
    notifyChange(newBlocks);
  };

  // --- Pegado de tabla desde Word / Excel en el diálogo ---
  const handlePasteInDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPasteError('');
    const parsed = parseTableFromClipboardData(e.clipboardData);
    if (parsed) {
      setPastePreviewTable(parsed);
    } else {
      setPasteError(
        'No se detectó una tabla válida en el portapapeles. Asegúrate de seleccionar y copiar una tabla completa en Word o Excel.'
      );
    }
  };

  const handleConfirmPasteTable = () => {
    if (pastePreviewTable) {
      handleAddTableBlock(pastePreviewTable);
      setPasteDialogOpen(false);
      setPastePreviewTable(null);
      setPasteError('');
      setToastMessage(
        `✅ ¡Tabla de Word importada con éxito! (${pastePreviewTable.headers.length} columnas, ${pastePreviewTable.rows.length} filas)`
      );
      setToastOpen(true);
    }
  };

  // --- Pegado directo global dentro del editor ---
  const handleGlobalPaste = (e) => {
    // Si el diálogo modal para pegar está abierto, ignorar para evitar duplicaciones
    if (pasteDialogOpen) return;

    // Si el usuario pega una tabla HTML completa de Word o celdas TSV con tabuladores
    const parsed = parseTableFromClipboardData(e.clipboardData);
    if (parsed && (parsed.headers.length >= 2 || parsed.rows.length >= 2)) {
      // Verificar si el foco está en un input pequeño de texto para no interceptar palabras sueltas
      const targetTag = e.target.tagName.toLowerCase();
      const isInput = targetTag === 'input';
      const isCellInput = isInput && e.target.placeholder?.includes('Fila');

      if (!isInput || isCellInput) {
        e.preventDefault();
        e.stopPropagation();
        handleAddTableBlock(parsed);
        setToastMessage(
          `✅ ¡Tabla de Word pegada directamente! (${parsed.headers.length} columnas, ${parsed.rows.length} filas)`
        );
        setToastOpen(true);
      }
    }
  };

  return (
    <Paper
      elevation={0}
      onPaste={handleGlobalPaste}
      sx={{
        border: '1.5px solid #dcdfe4',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      {/* Barra de herramientas superior estilo Word */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          p: 1,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: '#475569',
              mr: 1,
              display: { xs: 'none', sm: 'inline-block' },
            }}
          >
            Herramientas Word:
          </Typography>

          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setPastePreviewTable(null);
              setPasteError('');
              setPasteDialogOpen(true);
            }}
            startIcon={<ContentPasteIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              color: '#ffffff',
              bgcolor: '#0d9488',
              '&:hover': { bgcolor: '#0f766e' },
            }}
          >
            📋 Pegar Tabla de Word
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={() => handleAddTableBlock()}
            startIcon={<TableChartIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#4338ca',
              borderColor: '#c7d2fe',
              bgcolor: '#eef2ff',
              '&:hover': { bgcolor: '#e0e7ff', borderColor: '#818cf8' },
            }}
          >
            + Nueva Tabla
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={handleAddTextBlock}
            startIcon={<TextFieldsIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#0369a1',
              borderColor: '#bae6fd',
              bgcolor: '#f0f9ff',
              '&:hover': { bgcolor: '#e0f2fe', borderColor: '#38bdf8' },
            }}
          >
            + Texto / Párrafo
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={handleAddCalloutBlock}
            startIcon={<LightbulbIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#b45309',
              borderColor: '#fde68a',
              bgcolor: '#fffbeb',
              '&:hover': { bgcolor: '#fef3c7', borderColor: '#f59e0b' },
            }}
          >
            + Cuadro Destacado
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={handleAddBulletListBlock}
            startIcon={<FormatListBulletedIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#15803d',
              borderColor: '#bbf7d0',
              bgcolor: '#f0fdf4',
              '&:hover': { bgcolor: '#dcfce7', borderColor: '#4ade80' },
            }}
          >
            + Viñetas
          </Button>
        </Box>

        {/* Alternador de Vista (Edición vs Previsualización) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            sx={{ height: 32 }}
          >
            <ToggleButton value="edit" sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.78rem' }}>
              <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Editar
            </ToggleButton>
            <ToggleButton value="preview" sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.78rem' }}>
              <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Vista Previa
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ÁREA DE CONTENIDO */}
      <Box sx={{ p: 2, minHeight: 180, bgcolor: '#ffffff' }}>
        {viewMode === 'preview' ? (
          // VISTA PREVIA (Aspecto final formal)
          <Box
            sx={{
              p: 2.5,
              border: '1px dashed #cbd5e1',
              borderRadius: 1.5,
              bgcolor: '#fafafa',
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1.5, letterSpacing: 0.5 }}
            >
              PREVISUALIZACIÓN DOCUMENTAL (Así aparecerá en el PDF y firmas):
            </Typography>

            {data.blocks.map((block, idx) => {
              if (block.type === 'text') {
                return (
                  <Typography
                    key={block.id || idx}
                    variant="body2"
                    sx={{ color: '#1e293b', lineHeight: 1.7, mb: 1.5, whiteSpace: 'pre-line' }}
                  >
                    {block.content || <em style={{ color: '#94a3b8' }}>(Párrafo sin contenido)</em>}
                  </Typography>
                );
              }

              if (block.type === 'table') {
                const hasValidTitle =
                  block.title &&
                  block.title.trim() !== '' &&
                  !block.title.toLowerCase().includes('tabla importada') &&
                  block.title.toLowerCase() !== 'tabla de datos';

                return (
                  <Box key={block.id || idx} sx={{ my: 2 }}>
                    {hasValidTitle && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.5 }}>
                        {block.title}
                      </Typography>
                    )}
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ border: '1.5px solid #334155', borderRadius: 1 }}
                    >
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                          <TableRow>
                            {block.headers.map((h, hIdx) => (
                              <TableCell
                                key={hIdx}
                                sx={{
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  fontSize: '0.8rem',
                                  borderRight: '1px solid #cbd5e1',
                                  borderBottom: '1.5px solid #334155',
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {block.rows.map((row, rIdx) => (
                            <TableRow key={rIdx} sx={{ bgcolor: rIdx % 2 === 1 ? '#f8fafc' : '#ffffff' }}>
                              {row.map((cell, cIdx) => (
                                <TableCell
                                  key={cIdx}
                                  sx={{
                                    fontSize: '0.8rem',
                                    color: '#334155',
                                    borderRight: '1px solid #e2e8f0',
                                    borderBottom: '1px solid #e2e8f0',
                                  }}
                                >
                                  {cell || '-'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              }

              if (block.type === 'callout') {
                const borderColors = {
                  info: '#3b82f6',
                  success: '#10b981',
                  warning: '#f59e0b',
                };
                const bgColors = {
                  info: '#eff6ff',
                  success: '#ecfdf5',
                  warning: '#fffbeb',
                };
                return (
                  <Box
                    key={block.id || idx}
                    sx={{
                      my: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      borderLeft: `4px solid ${borderColors[block.variant] || '#3b82f6'}`,
                      bgcolor: bgColors[block.variant] || '#eff6ff',
                    }}
                  >
                    {block.title && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.3 }}>
                        {block.title}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.5 }}>
                      {block.content}
                    </Typography>
                  </Box>
                );
              }

              if (block.type === 'bullet_list') {
                return (
                  <Box component="ul" key={block.id || idx} sx={{ pl: 3, my: 1 }}>
                    {block.items.map((item, iIdx) => (
                      <Typography component="li" variant="body2" key={iIdx} sx={{ color: '#334155', mb: 0.5 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                );
              }

              return null;
            })}
          </Box>
        ) : (
          // MODO EDICIÓN INTERACTIVA
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data.blocks.map((block, index) => (
              <Paper
                key={block.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  borderColor: '#e2e8f0',
                  bgcolor: '#ffffff',
                  position: 'relative',
                  '&:hover': { borderColor: '#94a3b8', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' },
                }}
              >
                {/* Cabecera del bloque con controles de orden y borrado */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.2,
                    pb: 0.8,
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={
                        block.type === 'text'
                          ? `Párrafo ${index + 1}`
                          : block.type === 'table'
                          ? `📊 Tabla ${index + 1}`
                          : block.type === 'callout'
                          ? `💡 Cuadro Destacado ${index + 1}`
                          : `📋 Viñetas ${index + 1}`
                      }
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        bgcolor:
                          block.type === 'table'
                            ? '#e0e7ff'
                            : block.type === 'callout'
                            ? '#fef3c7'
                            : block.type === 'bullet_list'
                            ? '#dcfce7'
                            : '#f1f5f9',
                        color:
                          block.type === 'table'
                            ? '#3730a3'
                            : block.type === 'callout'
                            ? '#92400e'
                            : block.type === 'bullet_list'
                            ? '#166534'
                            : '#475569',
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Subir bloque">
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => handleMoveBlock(index, -1)}
                          sx={{ p: 0.4 }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bajar bloque">
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === data.blocks.length - 1}
                          onClick={() => handleMoveBlock(index, 1)}
                          sx={{ p: 0.4 }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar bloque">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveBlock(block.id)}
                        sx={{ p: 0.4 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* CONTENIDO DEL BLOQUE SEGÚN TIPO */}
                {block.type === 'text' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={
                      placeholder ||
                      'Indicar el propósito de la reunión, comenzando con un verbo en infinitivo (por ejemplo: analizar, definir, revisar)...'
                    }
                    value={block.content}
                    onChange={(e) => handleUpdateText(block.id, e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fafafa',
                        fontSize: '0.88rem',
                      },
                    }}
                  />
                )}

                {block.type === 'table' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Título y acciones rápidas de la tabla */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder="Título opcional de la tabla (dejar vacío si no desea título)"
                        value={
                          block.title &&
                          !block.title.toLowerCase().includes('tabla importada') &&
                          block.title.toLowerCase() !== 'tabla de datos'
                            ? block.title
                            : ''
                        }
                        onChange={(e) => handleUpdateTableTitle(block.id, e.target.value)}
                        sx={{ flex: 1, minWidth: 220, '& input': { fontWeight: 600, fontSize: '0.85rem' } }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddTableCol(block.id)}
                          startIcon={<AddIcon fontSize="small" />}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          + Columna
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddTableRow(block.id)}
                          startIcon={<AddIcon fontSize="small" />}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          + Fila
                        </Button>
                      </Box>
                    </Box>

                    {/* Grilla editable tipo hoja de cálculo / Word */}
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{
                        border: '1.5px solid #6366f1',
                        borderRadius: 1.5,
                        overflowX: 'auto',
                        boxShadow: '0 2px 4px rgba(99, 102, 241, 0.05)',
                      }}
                    >
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f3ff' }}>
                          <TableRow>
                            {block.headers.map((headerText, colIdx) => (
                              <TableCell
                                key={colIdx}
                                sx={{
                                  p: 1,
                                  borderRight: '1px solid #e0e7ff',
                                  borderBottom: '2px solid #6366f1',
                                  minWidth: 140,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="standard"
                                    value={headerText}
                                    onChange={(e) => handleUpdateTableHeader(block.id, colIdx, e.target.value)}
                                    placeholder={`Encabezado ${colIdx + 1}`}
                                    InputProps={{
                                      disableUnderline: false,
                                      sx: { fontWeight: 700, fontSize: '0.8rem', color: '#4338ca' },
                                    }}
                                  />
                                  {block.headers.length > 1 && (
                                    <Tooltip title="Eliminar columna">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveTableCol(block.id, colIdx)}
                                        sx={{ p: 0.2, color: '#a5b4fc', '&:hover': { color: '#ef4444' } }}
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            ))}
                            <TableCell sx={{ width: 40, p: 0.5, borderBottom: '2px solid #6366f1' }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {block.rows.map((row, rowIdx) => (
                            <TableRow key={rowIdx} sx={{ bgcolor: rowIdx % 2 === 1 ? '#faf5ff' : '#ffffff' }}>
                              {row.map((cellValue, colIdx) => (
                                <TableCell
                                  key={colIdx}
                                  sx={{
                                    p: 0.8,
                                    borderRight: '1px solid #f1f5f9',
                                    borderBottom: '1px solid #f1f5f9',
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    value={cellValue}
                                    onChange={(e) => handleUpdateTableCell(block.id, rowIdx, colIdx, e.target.value)}
                                    placeholder={`Fila ${rowIdx + 1}, Col ${colIdx + 1}`}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        bgcolor: '#ffffff',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: 'transparent' },
                                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                                      },
                                    }}
                                  />
                                </TableCell>
                              ))}
                              <TableCell sx={{ width: 40, p: 0.5, textAlign: 'center' }}>
                                {block.rows.length > 1 && (
                                  <Tooltip title="Eliminar fila">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveTableRow(block.id, rowIdx)}
                                      sx={{ p: 0.3, color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {block.type === 'callout' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="Título del cuadro destacado"
                        value={block.title || ''}
                        onChange={(e) => handleUpdateCallout(block.id, 'title', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <ToggleButtonGroup
                        size="small"
                        value={block.variant || 'info'}
                        exclusive
                        onChange={(_, newVar) => newVar && handleUpdateCallout(block.id, 'variant', newVar)}
                        sx={{ height: 38 }}
                      >
                        <ToggleButton value="info" sx={{ px: 1, fontSize: '0.75rem', textTransform: 'none' }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: '#3b82f6' }} />
                          Nota
                        </ToggleButton>
                        <ToggleButton value="success" sx={{ px: 1, fontSize: '0.75rem', textTransform: 'none' }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5, color: '#10b981' }} />
                          Éxito
                        </ToggleButton>
                        <ToggleButton value="warning" sx={{ px: 1, fontSize: '0.75rem', textTransform: 'none' }}>
                          <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5, color: '#f59e0b' }} />
                          Alerta
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Escriba aquí el contenido a destacar visualmente..."
                      value={block.content}
                      onChange={(e) => handleUpdateCallout(block.id, 'content', e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor:
                            block.variant === 'success'
                              ? '#f0fdf4'
                              : block.variant === 'warning'
                              ? '#fffbeb'
                              : '#f0f9ff',
                        },
                      }}
                    />
                  </Box>
                )}

                {block.type === 'bullet_list' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {block.items.map((item, itemIdx) => (
                      <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#64748b', fontWeight: 700 }}>•</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`Punto ${itemIdx + 1}`}
                          value={item}
                          onChange={(e) => handleUpdateBulletItem(block.id, itemIdx, e.target.value)}
                          variant="outlined"
                          sx={{ '& input': { fontSize: '0.85rem' } }}
                        />
                        {block.items.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveBulletItem(block.id, itemIdx)}
                            sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => handleAddBulletItem(block.id)}
                      sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontSize: '0.78rem',
                        color: '#166534',
                        fontWeight: 600,
                      }}
                    >
                      + Agregar viñeta
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Diálogo modal para pegar tabla desde Word o Excel */}
      <Dialog
        open={pasteDialogOpen}
        onClose={() => {
          setPasteDialogOpen(false);
          setPastePreviewTable(null);
          setPasteError('');
        }}
        onPaste={(e) => e.stopPropagation()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <ContentPasteIcon sx={{ color: '#059669' }} />
          <Typography variant="h6" fontWeight={700}>
            Pegar Tabla desde Word o Excel
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copia cualquier tabla desde <strong>Microsoft Word</strong> o <strong>Excel</strong> (selecciona la tabla y presiona <code>Ctrl + C</code> o <code>Cmd + C</code>), luego haz clic en el cuadro inferior y presiona <code>Ctrl + V</code>.
          </Typography>

          <Box
            onPaste={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePasteInDialog(e);
            }}
            tabIndex={0}
            sx={{
              border: '2px dashed',
              borderColor: pastePreviewTable ? '#10b981' : '#6366f1',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: pastePreviewTable ? '#f0fdf4' : '#f8fafc',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              '&:focus': {
                borderColor: '#4338ca',
                boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.2)',
              },
            }}
          >
            {!pastePreviewTable ? (
              <Box sx={{ py: 3 }}>
                <ContentPasteIcon sx={{ fontSize: 48, color: '#6366f1', mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                  Haz clic aquí y presiona Ctrl + V para pegar la tabla
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  El sistema extraerá automáticamente los encabezados, filas y columnas de Word
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'left' }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <strong>¡Tabla detectada exitosamente!</strong> Se identificaron{' '}
                  <strong>{pastePreviewTable.headers.length} columnas</strong> y{' '}
                  <strong>{pastePreviewTable.rows.length} filas</strong> de datos.
                </Alert>

                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>
                  VISTA PREVIA DE LA TABLA IMPORTADA:
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                      <TableRow>
                        {pastePreviewTable.headers.map((h, i) => (
                          <TableCell key={i} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pastePreviewTable.rows.map((row, rIdx) => (
                        <TableRow key={rIdx} sx={{ bgcolor: rIdx % 2 === 1 ? '#f8fafc' : '#ffffff' }}>
                          {row.map((cell, cIdx) => (
                            <TableCell key={cIdx} sx={{ fontSize: '0.8rem' }}>
                              {cell || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {pasteError && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                {pasteError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasteDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!pastePreviewTable}
            onClick={handleConfirmPasteTable}
            startIcon={<CheckIcon />}
            sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, fontWeight: 700 }}
          >
            Insertar Tabla en el Objetivo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificación rápida */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default ObjetivoRichEditor;
